(() => {
  if (customElements.get('a2l-official-module')) return;

  class A2lOfficialModule extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      this.events?.abort();
      this.events = new AbortController();
      const signal = this.events.signal;
      this.mobile = matchMedia(['focus', 'tabs'].includes(this.dataset.layout) ? '(max-width: 1024px)' : '(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.videos = [...this.querySelectorAll('video')];
      this.videos.filter((video) => video.hasAttribute('data-decorative')).forEach((video) => { video.controls = false; });
      this.visibleVideos = new Set();
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.descriptions = [...this.querySelectorAll('[data-tab-description]')];
      this.motionMode = this.dataset.motion || 'auto';
      this.demoMotionMode = this.dataset.demoMotion || 'auto';

      if (this.tabs.length > 1 && this.tabs.length === this.panels.length) {
        const tablist = this.querySelector('[data-tabs]');
        tablist.hidden = false;
        tablist.setAttribute('role', 'tablist');
        this.classList.add('a2l-official--tabs-ready');
        this.tabs.forEach((tab) => {
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', tab.dataset.tab);
          const panel = this.panels.find((item) => item.id === tab.dataset.tab);
          const description = this.descriptions.find((item) => item.dataset.tabDescription === panel.id);
          if (description) {
            tab.setAttribute('aria-controls', panel.id + ' ' + description.id);
            panel.setAttribute('aria-describedby', description.id);
          }
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tab.id);
          panel.tabIndex = 0;
          tab.addEventListener('click', () => this.selectTab(tab), { signal });
          tab.addEventListener('keydown', (event) => {
            const index = this.tabs.indexOf(tab);
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = this.tabs.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            this.selectTab(this.tabs[next]);
            this.tabs[next].focus();
          }, { signal });
        });
        this.selectTab(this.tabs[0]);
      }

      this.setupSpecs(signal);
      this.setupA1Details(signal);
      this.setupReviewVideos(signal);

      if (this.dataset.layout === 'focus') {
        this.focusCards = [...this.querySelectorAll('[data-panel]')];
        this.focusCards.forEach((card) => {
          const button = card.querySelector('[data-focus-trigger]');
          if (!button) return;
          button.hidden = false;
          card.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'mouse' || event.pointerType === 'pen') this.selectFocusCard(card);
          }, { signal });
          button.addEventListener('click', () => this.selectFocusCard(card), { signal });
          button.addEventListener('focus', () => this.selectFocusCard(card), { signal });
          button.addEventListener('keydown', (event) => {
            const index = this.focusCards.indexOf(card);
            let next;
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.focusCards.length;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.focusCards.length) % this.focusCards.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = this.focusCards.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            this.focusCards[next].querySelector('[data-focus-trigger]').focus({ preventScroll: true });
          }, { signal });
        });
        this.selectFocusCard(this.focusCards[0]);
      }

      const reviews = this.dataset.layout === 'reviews';
      const gallery = this.dataset.layout === 'gallery' || reviews ? this.querySelector('[data-items]') : null;
      if (gallery && gallery.children.length > 1) {
        const controls = this.querySelector('[data-gallery-controls]');
        if (controls) {
          controls.hidden = false;
          const arrows = [...controls.querySelectorAll('[data-gallery-direction]')];
          const dots = [...controls.querySelectorAll('[data-gallery-index]')];
          const items = [...gallery.children];
          const progress = controls.querySelector('[data-review-progress]');
          const updateProgress = () => {
            if (progress) progress.style.setProperty('--a2lo-review-progress', `${Math.min(100, (gallery.scrollLeft + gallery.clientWidth) / gallery.scrollWidth * 100)}%`);
          };
          let selected = 0;
          let requested = null;
          const lastIndex = () => reviews ? Math.max(0, items.findIndex((item) => item.offsetLeft - items[0].offsetLeft >= gallery.scrollWidth - gallery.clientWidth - 1)) : items.length - 1;
          const render = (index) => {
            selected = index;
            gallery.dataset.activeIndex = String(index);
            dots.forEach((dot, i) => i === index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current'));
            arrows.forEach((arrow) => { arrow.disabled = Number(arrow.dataset.galleryDirection) < 0 ? index === 0 : index === lastIndex(); });
            updateProgress();
          };
          const select = (index) => {
            index = Math.max(0, Math.min(lastIndex(), index));
            if (reviews) this.resetReviewVideos();
            requested = index;
            render(index);
            clearTimeout(this.galleryScrollTimer);
            this.galleryScrollTimer = setTimeout(() => { requested = null; }, 180);
            gallery.scrollTo({ left: items[index].offsetLeft - items[0].offsetLeft, behavior: this.reduced.matches ? 'instant' : 'smooth' });
          };
          const synchronize = () => {
            updateProgress();
            if (requested !== null) {
              clearTimeout(this.galleryScrollTimer);
              this.galleryScrollTimer = setTimeout(() => { requested = null; }, 180);
              return;
            }
            const end = gallery.scrollWidth - gallery.clientWidth;
            const index = gallery.scrollLeft >= end - 1 ? lastIndex() : items.reduce((best, item, i) => Math.abs(item.offsetLeft - items[0].offsetLeft - gallery.scrollLeft) < Math.abs(items[best].offsetLeft - items[0].offsetLeft - gallery.scrollLeft) ? i : best, 0);
            render(index);
          };
          arrows.forEach((arrow) => {
            arrow.hidden = dots.length > 0;
            arrow.addEventListener('click', () => select(selected + Number(arrow.dataset.galleryDirection)), { signal });
          });
          dots.forEach((dot, index) => dot.addEventListener('click', () => select(index), { signal }));
          controls.addEventListener('keydown', (event) => {
            const index = dots.indexOf(event.target.closest('[data-gallery-index]'));
            if (index < 0) return;
            let next;
            if (event.key === 'ArrowRight') next = Math.min(index + 1, dots.length - 1);
            if (event.key === 'ArrowLeft') next = Math.max(index - 1, 0);
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = dots.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            select(next);
            dots[next].focus();
          }, { signal });
          if (reviews) {
            gallery.addEventListener('keydown', (event) => {
              if (event.target !== gallery) return;
              const next = { ArrowRight: selected + 1, ArrowLeft: selected - 1, Home: 0, End: lastIndex() }[event.key];
              if (next === undefined) return;
              event.preventDefault();
              select(next);
            }, { signal });
            this.addEventListener('shopify:block:select', (event) => {
              const index = items.findIndex((item) => item.contains(event.target));
              if (index >= 0) select(index);
            }, { signal });
          }
          ['pointerdown', 'wheel'].forEach((name) => gallery.addEventListener(name, () => { requested = null; }, { passive: true, signal }));
          gallery.addEventListener('scroll', synchronize, { passive: true, signal });
          this.galleryResize = new ResizeObserver(synchronize);
          this.galleryResize.observe(gallery);
          synchronize();
        }
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting) this.visibleVideos.add(target);
            else this.visibleVideos.delete(target);
            this.updateVideo(target);
          });
        }, { threshold: 0.15 });
        this.videos.forEach((video) => this.observer.observe(video));
      }

      const updateAll = () => this.videos.forEach((video) => this.updateVideo(video));
      this.mobile.addEventListener('change', updateAll, { signal });
      this.reduced.addEventListener('change', updateAll, { signal });
      document.addEventListener('visibilitychange', updateAll, { signal });
      this.querySelectorAll('details').forEach((details) => details.addEventListener('toggle', updateAll, { signal }));
      this.addEventListener('shopify:block:select', (event) => {
        const block = event.target.closest('[data-panel], details');
        if (!block || !this.contains(block)) return;
        if (block.matches('details')) block.open = true;
        if (this.focusCards?.includes(block)) this.selectFocusCard(block);
        const tab = this.tabs.find((item) => item.dataset.tab === block.id);
        if (tab) this.selectTab(tab);
      }, { signal });
      this.videos.filter((video) => video.controls).forEach((video) => this.setSource(video));
      this.setupHero(signal);
    }

    setupReviewVideos(signal) {
      this.reviewObserver?.disconnect();
      if (this.dataset.layout !== 'reviews') return;
      if ('IntersectionObserver' in window) {
        this.reviewObserver = new IntersectionObserver((entries) => entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) this.resetReviewVideos(target);
        }));
        this.querySelectorAll('[data-review-video]').forEach((media) => this.reviewObserver.observe(media));
      }
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.resetReviewVideos(); }, { signal });
    }

    resetReviewVideos(container = this) {
      container.querySelectorAll('.a2l-official__review-video iframe, :scope > iframe').forEach((iframe) => {
        iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), 'https://www.youtube.com');
      });
    }

    setupA1Details(signal) {
      this.a1DetailsResize?.disconnect();
      cancelAnimationFrame(this.a1DetailsFrame);
      if (!this.matches('.a1-details.a2l-official--split')) return;
      const header = this.querySelector('.a2l-official__header');
      const media = this.querySelector('.a2l-official__items');
      const details = header?.querySelector('.a1-details__more');
      const summary = details?.querySelector('summary');
      if (!header || !media || !summary || !('ResizeObserver' in window)) return;
      const desktop = matchMedia('(min-width:1025px)');
      const align = () => {
        if (!this.isConnected || signal.aborted) return;
        if (!desktop.matches) {
          this.classList.remove('a1-details--anchored');
          this.style.removeProperty('--a1-copy-offset');
          this.style.removeProperty('--a1-media-offset');
          return;
        }
        const padding = parseFloat(getComputedStyle(header).paddingTop) || 0;
        const expanded = details.open ? details.getBoundingClientRect().height - summary.getBoundingClientRect().height : 0;
        const closedHeight = header.getBoundingClientRect().height - padding - expanded;
        const mediaHeight = media.getBoundingClientRect().height;
        for (const [name, height] of [['copy', mediaHeight - closedHeight], ['media', closedHeight - mediaHeight]]) {
          const property = `--a1-${name}-offset`, offset = `${Math.max(0, height / 2)}px`;
          if (this.style.getPropertyValue(property) !== offset) this.style.setProperty(property, offset);
        }
        this.classList.add('a1-details--anchored');
      };
      const schedule = () => {
        cancelAnimationFrame(this.a1DetailsFrame);
        this.a1DetailsFrame = requestAnimationFrame(align);
      };
      this.a1DetailsResize = new ResizeObserver(schedule);
      this.a1DetailsResize.observe(header);
      this.a1DetailsResize.observe(media);
      desktop.addEventListener('change', schedule, { signal });
      document.fonts?.ready.then(schedule);
      align();
    }

    setupSpecs(signal) {
      const wrapper = this.querySelector('[data-spec-table]');
      const button = this.querySelector('[data-spec-toggle]');
      const table = wrapper?.querySelector('table');
      if (!table || !button) return;
      const preview = Number(wrapper.dataset.previewGroups) || 4;
      let groups = 0;
      this.extraSpecRows = [];
      [...table.tBodies].flatMap((body) => [...body.rows]).forEach((row) => {
        const first = row.cells[0];
        if (first && (first.rowSpan > 1 || row.cells.length >= 3 && first.colSpan === 1)) groups++;
        if (groups > preview) this.extraSpecRows.push(row);
      });
      if (!this.extraSpecRows.length) return;
      this.extraSpecRows.forEach((row) => { row.hidden = true; });
      button.hidden = false;
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        this.extraSpecRows.forEach((row) => { row.hidden = !expanded; });
        button.setAttribute('aria-expanded', String(expanded));
        if (!expanded) button.scrollIntoView({ block: 'center', behavior: this.reduced.matches ? 'instant' : 'smooth' });
      }, { signal });
    }

    selectFocusCard(selected) {
      this.focusCards.forEach((card) => {
        const active = card === selected;
        card.toggleAttribute('data-focus-active', active);
        card.querySelector('[data-focus-trigger]')?.setAttribute('aria-pressed', String(active));
      });
      this.videos?.forEach((video) => this.updateVideo(video));
    }

    setupHero(signal) {
      if (this.dataset.layout !== 'hero') return;
      const button = this.querySelector('[data-hero-play]');
      const details = this.querySelector('.a2l-official__media-disclosure:has(video[controls])');
      if (!button || !details) return;
      this.heroVideo = details.querySelector('video');
      this.heroDetails = details;
      details.dataset.heroVideo = '';
      details.hidden = true;
      details.open = false;
      this.classList.add('a2l-official--hero-ready');
      this.classList.remove('a2l-official--hero-playing');
      button.hidden = false;
      let feedback = details.querySelector('.a2l-official__hero-feedback');
      if (!feedback) {
        feedback = document.createElement('div');
        feedback.className = 'a2l-official__hero-feedback';
        feedback.hidden = true;
        const message = document.createElement('span');
        message.setAttribute('role', 'status');
        message.textContent = 'No se pudo iniciar el video.';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Reintentar';
        feedback.append(message, retry);
        details.append(feedback);
      }
      this.heroFeedback = feedback;
      button.addEventListener('click', () => this.playHero(), { signal });
      feedback.querySelector('button').addEventListener('click', () => this.playHero(), { signal });
      this.heroVideo.addEventListener('playing', () => { feedback.hidden = true; }, { signal });
    }

    playHero() {
      this.heroDetails.hidden = false;
      this.heroDetails.open = true;
      this.classList.add('a2l-official--hero-playing');
      this.heroFeedback.hidden = true;
      this.setSource(this.heroVideo);
      this.heroVideo.muted = false;
      this.heroVideo.controls = true;
      this.heroVideo.focus({ preventScroll: true });
      this.heroVideo.play().catch(() => { this.heroFeedback.hidden = false; });
    }

    selectTab(selected) {
      const changed = this.selectedTab && this.selectedTab !== selected;
      this.tabAnimations?.forEach((animation) => animation.cancel());
      this.tabAnimations = [];
      this.selectedTab = selected;
      this.tabs.forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        const panel = this.panels.find((item) => item.id === tab.dataset.tab);
        if (panel) panel.hidden = !active;
      });
      this.descriptions.forEach((description) => {
        const active = description.dataset.tabDescription === selected.dataset.tab;
        description.setAttribute('aria-hidden', String(!active));
        description.inert = !active;
      });
      if (changed && this.motionMode !== 'off' && (this.motionMode === 'always' || !this.reduced.matches)) {
        const panel = this.panels.find((item) => item.id === selected.dataset.tab);
        const description = this.descriptions.find((item) => item.dataset.tabDescription === selected.dataset.tab);
        [panel, description].filter(Boolean).forEach((item) => {
          this.tabAnimations.push(item.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 500, easing: 'ease-out' }));
        });
      }
      this.videos?.forEach((video) => this.updateVideo(video));
    }

    setSource(video) {
      const src = this.mobile.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
      const poster = this.mobile.matches ? video.dataset.mobilePoster : video.dataset.desktopPoster;
      if (poster) video.poster = poster;
      if (src && video.dataset.loadedSrc !== src) {
        video.pause();
        video.src = src;
        video.dataset.loadedSrc = src;
        video.load();
      }
    }

    updateVideo(video) {
      if (video.hasAttribute('data-decorative')) video.controls = false;
      const closedDetails = [...this.querySelectorAll('details:not([open])')].some((details) => details.contains(video));
      const heroRect = video === this.heroVideo ? video.getBoundingClientRect() : null;
      const visible = heroRect ? heroRect.bottom > 0 && heroRect.top < innerHeight : this.visibleVideos.has(video);
      const available = !document.hidden && !video.closest('[hidden]') && !closedDetails && visible;
      if (!available) {
        video.pause();
        return;
      }
      if (video.controls) {
        this.setSource(video);
        return;
      }
      if (this.demoMotionMode === 'off' || this.demoMotionMode !== 'always' && this.reduced.matches) {
        video.pause();
        return;
      }
      this.setSource(video);
      video.muted = true;
      video.loop = true;
      video.play().catch(() => {});
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.observer?.disconnect();
      this.galleryResize?.disconnect();
      this.reviewObserver?.disconnect();
      this.resetReviewVideos();
      this.a1DetailsResize?.disconnect();
      cancelAnimationFrame(this.a1DetailsFrame);
      clearTimeout(this.galleryScrollTimer);
      this.tabAnimations?.forEach((animation) => animation.cancel());
      this.extraSpecRows?.forEach((row) => { row.hidden = false; });
      this.videos?.forEach((video) => video.pause());
    }
  }

  customElements.define('a2l-official-module', A2lOfficialModule);
})();

